import { Controller } from "@hotwired/stimulus"
import { applyLoginReturnTo, notifySessionExpired, responseIndicatesSessionExpired } from "../lib/session_expired.mjs"

// Connects to data-controller="drawer"
// Supports stacking multiple drawers - opening a drawer while one is already open
// creates a new drawer on top, preserving scroll position and state of the previous one
// Uses the DOM as the single source of truth - no separate JavaScript state tracking
export default class extends Controller {
  static targets = ["drawerStack", "mainContent"];

  // https://stackoverflow.com/questions/57318097/stimulus-controller-event-listened-multiple-times-how-do-i-remove-event-listen
  initialize() {
    this.boundOpen = this.open.bind(this)
    this.boundClose = this.close.bind(this)
    this.boundCloseAll = this.closeAll.bind(this)
    this.boundExpand = this.expand.bind(this)
    this.boundCollapse = this.collapse.bind(this)
  }

  connect() {
    document.addEventListener('drawer.open', this.boundOpen);
    document.addEventListener('drawer.close', this.boundClose);
    document.addEventListener('drawer.closeAll', this.boundCloseAll);
    document.addEventListener('drawer.expand', this.boundExpand);
    document.addEventListener('drawer.collapse', this.boundCollapse);

    this.openClasses = ["max-w-full", "opacity-100"];
    this.closedClasses = ["w-0", "opacity-0"];


    // auto open a drawer if the query params include "drawer=<url>"
    var params = new URLSearchParams(window.location.search)
    var drawerUrl = params.get("drawer")
    params.delete("drawer")

    var path = window.location.pathname
    var pathAndParams = path
    if (params.entries.length > 0) {
      pathAndParams += "?" + params.toString()
    }

    if (drawerUrl !== null) {
      // we want to remove the drawer from the URL so no future connect's open it again, causing an infinite loop
      window.history.pushState(null, null, pathAndParams)

      this.open({ url: drawerUrl })
    }
  }

  disconnect() {
    document.removeEventListener('drawer.open', this.boundOpen)
    document.removeEventListener('drawer.close', this.boundClose)
    document.removeEventListener('drawer.closeAll', this.boundCloseAll)
    document.removeEventListener('drawer.expand', this.boundExpand)
    document.removeEventListener('drawer.collapse', this.boundCollapse)
  }

  open(event) {
    if (this.opening) {
      return;
    }
    this.opening = true;

    // Clean up any closed panels (opacity-0) that are still in DOM
    const allPanels = Array.from(this.drawerStackTarget.querySelectorAll('[data-drawer-target="panel"]'));
    const closedPanels = allPanels.filter(p => p.classList.contains('opacity-0'));
    closedPanels.forEach(panel => panel.remove());

    // Get template and clone it
    const template = document.getElementById('drawer-template');
    const templatePanel = template.querySelector('[data-drawer-target="panel"]');
    const newPanel = templatePanel.cloneNode(true);

    // Append to stack (panel starts with closed classes from template)
    this.drawerStackTarget.appendChild(newPanel);

    // Get references to elements in the new panel
    const panel = newPanel;
    const spinner = panel.querySelector('[data-drawer-target="spinner"]');
    const providerDetailsSkeleton = panel.querySelector('[data-drawer-target="providerDetailsSkeleton"]');
    const markDrawerSkeleton = panel.querySelector('[data-drawer-target="markDrawerSkeleton"]');
    const body = panel.querySelector('[data-drawer-target="body"]');
    const errorTemplate = panel.querySelector('[data-drawer-target="errorTemplate"]');
    const sessionExpiredTemplate = panel.querySelector('[data-drawer-target="sessionExpiredTemplate"]');

    // Determine if this is a provider details drawer (matches /search/:npi/details pattern)
    const isProviderDetails = /\/search\/\d+\/details/.test(event.url || '');
    // Determine if this is the Provider Intelligence "Update mark" drawer
    // (matches /provider_intelligence/:scope/:id/edit)
    const isMarkDrawer = /\/provider_intelligence\/(location|ein)\/[^/]+\/edit/.test(event.url || '');

    // Guard rail: the drawer panel itself should never be a scrolling container.
    // Some browsers will scroll ancestor containers during focus/scrollIntoView on form controls,
    // which can shift the entire drawer contents out of view (appearing "blank").
    // Lock the panel scroll position to the top.
    panel.scrollTop = 0;
    panel.addEventListener(
      "scroll",
      () => {
        if (panel.scrollTop !== 0) panel.scrollTop = 0;
      },
      { passive: true }
    );

    // Check if this is the first drawer
    const openPanels = this.getAllPanels();
    const isFirstDrawer = (openPanels.length === 0);

    body.classList.add("hidden");
    
    // Show provider details skeleton for provider details URLs, the mark-drawer
    // skeleton for Update mark URLs, otherwise show the generic spinner.
    if (isProviderDetails && providerDetailsSkeleton) {
      spinner.classList.add("hidden");
      providerDetailsSkeleton.classList.remove("hidden");
      if (markDrawerSkeleton) markDrawerSkeleton.classList.add("hidden");
    } else if (isMarkDrawer && markDrawerSkeleton) {
      spinner.classList.add("hidden");
      markDrawerSkeleton.classList.remove("hidden");
      if (providerDetailsSkeleton) providerDetailsSkeleton.classList.add("hidden");
    } else {
      spinner.classList.remove("hidden");
      if (providerDetailsSkeleton) {
        providerDetailsSkeleton.classList.add("hidden");
      }
      if (markDrawerSkeleton) {
        markDrawerSkeleton.classList.add("hidden");
      }
    }

    // Wait for browser to paint the initial closed state, then animate open
    // This ensures the slide-in animation is visible
    requestAnimationFrame(() => {
      // Animate drawer sliding open
      this.closedClasses.forEach(cls => panel.classList.remove(cls));
      this.openClasses.forEach(cls => panel.classList.add(cls));
      panel.style.width = "var(--drawer-width)";

      // Only adjust main content margin for the first drawer
      if (isFirstDrawer) {
        this.mainContentTarget.style.marginRight = "var(--drawer-width)";
      }
    });

    // Build URL with context params if provided
    var url = event.url;
    if (event.context && event.context.length > 0) {
      // The context is a JSON string.  We need to parse it and convert URL params.
      var context = JSON.parse(event.context);
      var params = new URLSearchParams(context);
      if (url.indexOf("?") > -1) {
        url += "&" + params.toString();
      } else {
        url += "?" + params.toString();
      }
    }

    // Fetch and load drawer content (with retry for resource exhaustion)
    this.fetchWithRetry(url, {
      maxRetries: 5,
      baseDelay: 1000,
      onSuccess: (response) => {
        body.innerHTML = response;

        // Check if the content uses the new flex-based drawer_content layout
        const usesFlexLayout = body.querySelector('[data-drawer-layout="flex"]');

        if (usesFlexLayout) {
          // New drawer_content handles its own scrolling via flexbox
          body.classList.remove('overflow-auto');
        } else {
          // Legacy drawers need overflow-auto for scrolling (ensure it's present)
          body.classList.add('overflow-auto');
        }

        spinner.classList.add("hidden");
        if (providerDetailsSkeleton) {
          providerDetailsSkeleton.classList.add("hidden");
        }
        if (markDrawerSkeleton) {
          markDrawerSkeleton.classList.add("hidden");
        }
        body.classList.remove("hidden");
        this.opening = false;
      },
      onError: (xhr) => {
        // If the fetch failed because the session expired, show the "log back in"
        // drawer body (and the global toast) instead of the generic error, so the
        // user understands they were signed out rather than that something broke.
        const sessionExpired = responseIndicatesSessionExpired(
          xhr && xhr.status,
          (name) => xhr && xhr.getResponseHeader(name)
        );
        if (sessionExpired) {
          notifySessionExpired();
          body.innerHTML = (sessionExpiredTemplate || errorTemplate).innerHTML;
          applyLoginReturnTo(body); // fill the "Log back in" return_to from window.location
        } else {
          body.innerHTML = errorTemplate.innerHTML;
        }
        body.classList.remove('overflow-auto'); // Error template uses flex layout
        spinner.classList.add("hidden");
        if (providerDetailsSkeleton) {
          providerDetailsSkeleton.classList.add("hidden");
        }
        if (markDrawerSkeleton) {
          markDrawerSkeleton.classList.add("hidden");
        }
        body.classList.remove("hidden");
        this.opening = false;
      }
    });
  }

  // Fetch a URL with automatic retry on resource exhaustion (ERR_INSUFFICIENT_RESOURCES).
  // When the browser runs out of connections (status 0), waits and retries with exponential
  // backoff instead of immediately showing an error.
  fetchWithRetry(url, { maxRetries = 5, baseDelay = 1000, attempt = 0, onSuccess, onError } = {}) {
    $.ajax({
      type: 'GET',
      url: url,
      success: onSuccess,
      error: (xhr, textStatus, errorThrown) => {
        // status 0 means the request never reached the server — typically
        // ERR_INSUFFICIENT_RESOURCES or a network-level failure. Retry these.
        const isResourceExhaustion = xhr.status === 0 && textStatus !== 'abort';

        if (isResourceExhaustion && attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt); // 1s, 2s, 4s, 8s, 16s
          console.warn(`[drawer] Fetch failed (attempt ${attempt + 1}/${maxRetries + 1}, status 0). Retrying in ${delay}ms...`, url);
          setTimeout(() => {
            this.fetchWithRetry(url, { maxRetries, baseDelay, attempt: attempt + 1, onSuccess, onError });
          }, delay);
        } else {
          onError(xhr, textStatus, errorThrown);
        }
      }
    });
  }

  close(event) {
    // if this comes from a button, it will still submit the form. if it doesn't come from a button, will this do anything bad? i don't think so.
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    // Get all OPEN panels from DOM - this is our source of truth
    const openPanels = this.getAllPanels();

    if (openPanels.length === 0) {
      return;
    }

    // The last open panel is always the current/top one
    const currentPanel = openPanels[openPanels.length - 1];

    // Animate current panel closing
    this.openClasses.forEach(cls => currentPanel.classList.remove(cls));
    this.closedClasses.forEach(cls => currentPanel.classList.add(cls));
    currentPanel.style.width = "";

    // After animation completes, remove the panel
    setTimeout(() => {
      currentPanel.remove();

      // If that was the last drawer, reset main content margin
      const remainingPanels = this.getAllPanels();

      if (remainingPanels.length === 0) {
        this.mainContentTarget.style.marginRight = "";
      }
    }, 500); // Match duration-500 from CSS animation
  }

  // Close ALL open drawers at once (e.g. after booking an appointment from a stacked drawer).
  // Each panel is animated closed; the main content margin resets after the last one is removed.
  closeAll(event) {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    const openPanels = this.getAllPanels();
    if (openPanels.length === 0) return;

    openPanels.forEach(panel => {
      this.openClasses.forEach(cls => panel.classList.remove(cls));
      this.closedClasses.forEach(cls => panel.classList.add(cls));
      panel.style.width = "";

      setTimeout(() => { panel.remove(); }, 500);
    });

    // Reset main content margin immediately (all drawers are closing)
    setTimeout(() => {
      this.mainContentTarget.style.marginRight = "";
    }, 500);
  }

  // Get all drawer panels from the stack (our single source of truth)
  // Only returns panels that are actually open (have opacity-100 class)
  getAllPanels() {
    const allPanels = Array.from(this.drawerStackTarget.querySelectorAll('[data-drawer-target="panel"]'));
    const openPanels = allPanels.filter(panel => panel.classList.contains('opacity-100'));
    return openPanels;
  }

  // Expand the current drawer to full screen width (for map view)
  expand() {
    const openPanels = this.getAllPanels();
    if (openPanels.length === 0) return;

    const currentPanel = openPanels[openPanels.length - 1];
    currentPanel.style.width = "100vw";

    // Also expand the inner wrapper that constrains content to --drawer-width
    const innerWrapper = currentPanel.querySelector('[data-drawer-target="innerWrapper"]');
    if (innerWrapper) innerWrapper.style.width = "100%";

    this.mainContentTarget.style.marginRight = "100vw";
  }

  // Collapse the current drawer back to normal width
  collapse() {
    const openPanels = this.getAllPanels();
    if (openPanels.length === 0) return;

    const currentPanel = openPanels[openPanels.length - 1];
    currentPanel.style.width = "var(--drawer-width)";

    // Reset the inner wrapper width
    const innerWrapper = currentPanel.querySelector('[data-drawer-target="innerWrapper"]');
    if (innerWrapper) innerWrapper.style.width = "var(--drawer-width)";

    // Only reset margin if this is the only drawer
    if (openPanels.length === 1) {
      this.mainContentTarget.style.marginRight = "var(--drawer-width)";
    }
  }
}
